import React, { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";

import useResponsiveState from "../../../../hooks/useResponsiveState";
import GraphTooltip from "./tooltips/GraphTooltip";

import "./graph.css";

const roundedTopRect = (x, y, w, h, r) => {
  r = Math.min(r, w / 2, h);
  if (h <= 0 || w <= 0) return "";
  return `
    M ${x},${y + h} L ${x},${y + r} a ${r},${r} 0 0 1 ${r},-${r}
    L ${x + w - r},${y} a ${r},${r} 0 0 1 ${r},${r} L ${x + w},${y + h} Z
  `;
};

export default function MomentMixChart({ config, onNodeSelect = () => {} }) {
  const chartRef = useRef(null);
  const { viewType } = useResponsiveState();

  const [tooltipState, setTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  // The tile is fluid now, so redraw when its box changes.
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = chartRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setChartSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const processedData = useMemo(() => {
    return Array.isArray(config.graphData) ? config.graphData : [];
  }, [config.graphData]);

  useEffect(() => {
    if (!chartRef.current || processedData.length === 0) return;

    const chartContainer = d3.select(chartRef.current);
    chartContainer.select("svg").remove();

    const margin =
      viewType === "mobile"
        ? config.margin.mobile || { top: 20, right: 20, bottom: 40, left: 40 }
        : config.margin.desktop || { top: 40, right: 60, bottom: 60, left: 20 };

    const width =
      chartContainer.node().getBoundingClientRect().width -
      margin.left -
      margin.right;
    const height =
      chartContainer.node().getBoundingClientRect().height -
      margin.top -
      margin.bottom;

    const svg = chartContainer
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const y = d3.scaleLinear().domain([0, 1]).range([height, 0]);
    const x = d3.scaleBand().range([0, width]).padding(0.25);
    x.domain(processedData.map((d) => d.category));

    const step = x.step();
    const paddingOffset = (step - x.bandwidth()) / 2;

    const gridGroup = svg.append("g").attr("class", "grid-vertical");

    gridGroup
      .selectAll("line.mid-grid")
      .data(x.domain())
      .join("line")
      .attr("class", "mid-grid")
      .attr("x1", (d) => x(d) - paddingOffset)
      .attr("x2", (d) => x(d) - paddingOffset)
      .attr("y1", 0)
      .attr("y2", height)
      .attr("stroke", "rgba(125, 125, 125, 0.2)")
      .attr("stroke-dasharray", "4,4");

    const lastCategory = x.domain()[x.domain().length - 1];
    if (lastCategory) {
      gridGroup
        .append("line")
        .attr("x1", x(lastCategory) + x.bandwidth() + paddingOffset)
        .attr("x2", x(lastCategory) + x.bandwidth() + paddingOffset)
        .attr("y1", 0)
        .attr("y2", height)
        .attr("stroke", "rgba(125, 125, 125, 0.2)")
        .attr("stroke-dasharray", "4,4");
    }

    const xAxis = d3.axisBottom(x).tickSize(0).tickPadding(10);
    const xAxisGroup = svg
      .append("g")
      .attr("class", "axis x-axis")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis);

    xAxisGroup
      .selectAll("text")
      .attr("text-anchor", "start")
      .attr("x", -x.bandwidth() / 2)
      .attr("dx", "-0.3em");

    const yAxis = d3
      .axisRight(y)
      .ticks(5)
      .tickSize(0)
      .tickPadding(10)
      .tickFormat(d3.format(".0%"));

    svg
      .append("g")
      .attr("class", "axis y-axis")
      .attr("transform", `translate(${width}, 0)`)
      .call(yAxis)
      .call((g) => g.select(".domain").remove());

    const yGrid = d3.axisRight(y).tickSize(width).tickFormat("").ticks(5);

    svg
      .append("g")
      .attr("class", "axis y-axis")
      .call(yGrid)
      .call((g) => g.select(".domain").remove())
      .selectAll("line")
      .attr("stroke", "rgba(125, 125, 125, 0.1)");

    svg
      .append("rect")
      .attr("class", "chart-border")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "none")
      .attr("stroke", "rgba(125, 125, 125, 0.2)")
      .attr("stroke-width", 1);

    const mousemove = (event, d) => {
      const [mouseX, mouseY] = d3.pointer(event, chartRef.current);
      setTooltipState({
        visible: true,
        x: mouseX + 20,
        y: mouseY,
        data: d,
      });
    };

    const mouseleave = () => {
      setTooltipState((prev) => ({ ...prev, visible: false }));
    };

    const handleNodeClick = (event, d) => {
      event.stopPropagation();
      svg.selectAll(".data-node").classed("data-node-selected", false);
      d3.select(event.currentTarget).classed("data-node-selected", true);
      onNodeSelect(d);
    };

    const validBars = processedData.filter((d) => d.avgScorePct > 0);
    const maxVal = d3.max(validBars, (d) => d.avgScorePct);
    const minVal = d3.min(validBars, (d) => d.avgScorePct);

    svg
      .append("g")
      .selectAll("path")
      .data(processedData)
      .join("path")
      .attr("class", (d) => {
        if (validBars.length > 1 && (d.avgScorePct === maxVal || d.avgScorePct === minVal)) {
          return "data-node data-node-outlier";
        }
        return "data-node data-node-default";
      })
      .attr("d", (d) => {
        const xPos = x(d.category);
        const yPos = y(d.avgScorePct);
        const barWidth = x.bandwidth();
        const barHeight = height - y(d.avgScorePct);
        return roundedTopRect(xPos, yPos, barWidth, barHeight, 5);
      })
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave)
      .on("click", handleNodeClick);

    const lineGen = d3
      .line()
      .x((d) => x(d.category) + x.bandwidth() / 2)
      .y((d) => y(d.planFreqPct));

    svg
      .append("path")
      .datum(processedData)
      .attr("class", "data-line-momentmix")
      .attr("d", lineGen);

    svg
      .append("g")
      .selectAll("circle")
      .data(processedData)
      .join("circle")
      .attr("class", "data-dot-momentmix")
      .attr("cx", (d) => x(d.category) + x.bandwidth() / 2)
      .attr("cy", (d) => y(d.planFreqPct))
      .attr("r", 6)
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave)
      .on("click", handleNodeClick);
  }, [config, processedData, viewType, onNodeSelect, chartSize]);

  return (
    <div className="chart-area">
      <div className="chart-area__plot">
        <div ref={chartRef} className="chart-tile"></div>
        <GraphTooltip
          visible={tooltipState.visible}
          x={tooltipState.x}
          y={tooltipState.y}
          data={tooltipState.data}
          mode="momentMix"
        />
      </div>
      {config.xAxisTitle && (
        <div className="chart-axis-title">{config.xAxisTitle}</div>
      )}
    </div>
  );
}
